import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import {
  GRID_BOOLEAN_COL_DEF,
  GRID_SINGLE_SELECT_COL_DEF,
  GridEditModes,
  GridEditSingleSelectCell,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import { enUS, frFR } from '@mui/x-data-grid-pro/locales';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BooleanCell, { BOOLEAN_CELL_CLASS } from '@/components/Grid/BooleanCell';
import ToggleBooleanEditCell from '@/components/Grid/ToggleBooleanEditCell';
import { noopFunc } from '@/consts';

/**
 * The built-in select saves the picked option but leaves the cell in edit mode, so the user has
 * to click outside of it to commit. Leaving edit mode as soon as the value changes drops that
 * extra interaction, the same way the auto-stop recipe does for custom editors.
 * https://mui.com/x/react-data-grid/editing/#with-auto-stop
 * @param {import('@mui/x-data-grid-pro').GridRenderEditCellParams} params
 */
function AutoStopSingleSelectEditCell({ field, id, value = null, ...params }) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const isRowEditing = rootProps.editMode === GridEditModes.Row;
  const initialValue = useRef(value);

  useEffect(() => {
    if (!isRowEditing && value !== initialValue.current) {
      apiRef.current.stopCellEditMode({ id, field });
    }
  }, [apiRef, field, id, isRowEditing, value]);

  return <GridEditSingleSelectCell {...params} field={field} id={id} value={value} />;
}

AutoStopSingleSelectEditCell.propTypes = {
  field: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
};

// TODO: In v8, only the read-only icons move to the theme; `renderCell` stays, since editable
// boolean cells need the checkbox and the click target. `GridBooleanCell` reads them off slots:
// MuiDataGrid: {
//   defaultProps: {
//     slots: {
//       // More colorful and visible boolean icons (defaults are gray with no background)
//       booleanCellTrueIcon: () => <CheckCircle color="success" />,
//       booleanCellFalseIcon: () => <Cancel color="error" />,
//     },
//   },
// },
GRID_BOOLEAN_COL_DEF.cellClassName = BOOLEAN_CELL_CLASS;
GRID_BOOLEAN_COL_DEF.renderCell = params => <BooleanCell {...params} />;
GRID_BOOLEAN_COL_DEF.renderEditCell = params => <ToggleBooleanEditCell {...params} />;
GRID_SINGLE_SELECT_COL_DEF.renderEditCell = params => <AutoStopSingleSelectEditCell {...params} />;

const DEFAULT_MODE = /** @type { import('@mui/material').PaletteMode } */ ('dark');

const ThemeContext = createContext({ mode: DEFAULT_MODE, toggleTheme: noopFunc });
ThemeContext.displayName = 'ThemeContext';

export const useThemeContext = () => useContext(ThemeContext);

export function CustomThemeProvider({ children }) {
  const [mode, setMode] = useState(DEFAULT_MODE);
  const { i18n } = useTranslation();
  document.documentElement.lang = i18n.language;

  const customTheme = useMemo(
    () =>
      createTheme(
        {
          palette: {
            mode,
            ...(mode === 'light'
              ? {
                  primary: {
                    main: '#2563eb', // blue-600
                    light: '#3b82f6', // blue-500
                    dark: '#1d4ed8', // blue-700
                    contrastText: '#fff',
                  },
                  secondary: {
                    main: '#9333ea', // purple-600
                    light: '#a855f7', // purple-500
                    dark: '#7e22ce', // purple-700
                    contrastText: '#fff',
                  },
                  background: {
                    default: '#fff', // Slate-50 from TailwindCSS
                    paper: '#ffffff', // White or you can use Slate-50
                  },
                  text: {
                    primary: '#0f172a', // Slate-900 for main text
                    secondary: '#94a3b8', // Slate-400 for secondary text
                    disabled: '#cdcdcd',
                  },
                }
              : {
                  primary: {
                    main: '#3b82f6', // blue-500
                    light: '#60a5fa', // blue-400
                    dark: '#2563eb', // blue-600
                    contrastText: '#fff',
                  },
                  secondary: {
                    main: '#9333ea', // purple-600
                    light: '#a855f7', // purple-500
                    dark: '#7e22ce', // purple-700
                    contrastText: '#fff',
                  },
                  background: {
                    default: '#121212', // Slate-900 from TailwindCSS for dark backgrounds
                    paper: '#020617', // Slate-800 for a slightly lighter paper
                  },
                  text: {
                    primary: '#fff',
                    secondary: '#fff',
                    disabled: '#cdcdcd',
                  },
                }),
          },
          typography: {
            allVariants: {
              color: mode === 'dark' ? '#e0e0e0' : '#0f172a', // Ensuring all text is readable
            },
          },
          components: {
            // Empty Selects with displayEmpty still show "None" while MUI treats value="" as unfilled.
            // Always shrink labels and notch outlines so labels never overlap displayed values.
            MuiInputLabel: {
              defaultProps: { shrink: true },
            },
            MuiOutlinedInput: {
              defaultProps: { notched: true },
            },
            MuiDataGrid: {
              defaultProps: {
                // There's no way to restore the original column order, so when we start saving
                // table shape metadata, the user won't have any way to restore to default.
                // If we ever re-enable this, remember to disable it on volunteer-facing tables
                disableColumnReorder: true,
                // We don't currently (or rarely) have a need for row selection
                // So it only leads to less readable text in some scenarios (like links and chips)
                // Note: Not disableMultipleRowSelection, since it requires deliberate keyboard use
                disableRowSelectionOnClick: true,
                // Virtualization promises rendering performance and lower memory usage,
                // but prevents CTRL+F and causes micro-stutters.
                // Only enable on tables with very large datasets.
                disableVirtualization: true,
              },
              styleOverrides: {
                // An editing cell is padded by 1px and its input by another 16px, against the
                // 10px a cell in view mode uses, so text jumped ~7px sideways the moment editing
                // started. 9px + the cell's 1px lines the two up.
                editInputCell: { '& input': { paddingLeft: 9, paddingRight: 9 } },
                // The Data Grid gives editable cells no affordance of their own, so users can't
                // tell them apart from read-only ones.
                'cell--editable': ({ theme }) => ({
                  position: 'relative',
                  cursor: 'pointer',
                  // Editing starts on a double-click, which mobile browsers otherwise read as
                  // double-tap-to-zoom. `manipulation` keeps scrolling and pinch-zoom.
                  touchAction: 'manipulation',
                  // An input-like outline drawn inside the cell, rather than a background tint or
                  // a border on the cell itself: a tint would fight the per-run colors the
                  // schedule grid paints on its rows, and a border would shift the cell's text.
                  // Inset on every side so a row of editable cells reads as separate fields
                  // instead of one continuous grid line.
                  // Boolean cells opt out: they show a checkbox and change on a single click, so
                  // the "double-click into a field" language doesn't apply to them.
                  [`&:not(.${BOOLEAN_CELL_CLASS})::after`]: {
                    content: '""',
                    position: 'absolute',
                    inset: 2,
                    borderRadius: 4,
                    border: `1px dashed ${theme.palette.primary.main}`,
                    boxShadow: `inset 0 1px 3px ${alpha(
                      theme.palette.common.black,
                      theme.palette.mode === 'light' ? 0.12 : 0.4,
                    )}`,
                    pointerEvents: 'none',
                  },
                  [`&:not(.${BOOLEAN_CELL_CLASS}):hover::after`]: {
                    borderStyle: 'solid',
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                  // The editor draws its own input, so the affordance would double up
                  '&.MuiDataGrid-cell--editing::after': { content: 'none' },
                }),
              },
            },
            MuiTableCell: {
              styleOverrides: {
                // By default "small" Tables only reduce vertical spacing
                sizeSmall: ({ theme }) => ({
                  paddingLeft: theme.spacing(1),
                  paddingRight: theme.spacing(1),
                }),
              },
            },
            MuiAccordionSummary: {
              defaultProps: {
                expandIcon: <ExpandMoreIcon />,
              },
            },
          },
        },
        // Setup MUI X data-grid translations
        i18n.language === 'en' ? enUS : frFR,
      ),
    [mode, i18n.language],
  );

  const value = useMemo(
    () => ({
      mode,
      toggleTheme: () => setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light')),
    }),
    [mode, setMode],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={customTheme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
}

CustomThemeProvider.propTypes = { children: PropTypes.node.isRequired };
